/**
 * Geometry for the intro → progress-bar dismiss morph ("Take your website live").
 *
 * The four stacked intro stage boxes fly up and flatten into the top progress
 * cluster. Because `MobileIntro` and `MobileChrome` are SEPARATE components that
 * `MobileApp` swaps, the morph is a FLIP: both are co-mounted for one beat, the
 * resting rect of every source box (intro) and every destination slot (chrome) is
 * measured, and absolutely-positioned ghost clones fly between the MEASURED
 * coordinates — so nothing ever animates to a guessed position or teleports.
 *
 * Only GEOMETRY is measured here; every colour comes from themed tokens in the
 * ghost's styles (so the morph is light/dark-correct with no literals), and all
 * timing comes from `CUST_ANIM`.
 */
import { CUST_ANIM } from '../../../../theme/customizer-tokens';

/** Data attribute on each intro stage box (index 0..3), measured as the source. */
export const INTRO_STAGE_ATTR = 'data-intro-stage';
/** Data attribute on each chrome progress slot (bars 0..2 + globe 3), the target. */
export const CHROME_STAGE_ATTR = 'data-chrome-stage';

/** A single flying box. `hero` = the active orange box → "on" bar; `globe` keeps
 *  its glyph and dissolves its plate; `idle` = a grey box → "off" track bar. */
export type GhostKind = 'hero' | 'idle' | 'globe';

export interface MorphGhost {
  key: string;
  kind: GhostKind;
  /** Numeral to render (1/2/3), or null for the globe box (renders the glyph). */
  numeral: string | null;
  /** Resting CENTRE of the source box, relative to the morph container (px). */
  startCx: number;
  startCy: number;
  /** Translation from the source centre to the destination centre (px). */
  dx: number;
  dy: number;
  /** Source box size (px). */
  fromW: number;
  fromH: number;
  /** Height held through mid-flight so the box travels mostly square and only
   *  flattens late, as it decelerates into the rail (px). */
  midH: number;
  /** Destination slot size (px). */
  toW: number;
  toH: number;
  /** Per-box flight delay — the hero leads, the rest cascade (ms). */
  delay: number;
}

const kindFor = (i: number, count: number): GhostKind => (i === 0 ? 'hero' : i === count - 1 ? 'globe' : 'idle');

/**
 * Measure the source (intro) + destination (chrome) rects within `container` and
 * build the flight spec for each box. Returns null if either set is missing /
 * mismatched (the caller then just hard-swaps), so the morph never half-runs.
 */
export const measureGhosts = (container: HTMLElement): MorphGhost[] | null => {
  const sources = Array.from(container.querySelectorAll<HTMLElement>(`[${INTRO_STAGE_ATTR}]`));
  const targets = Array.from(container.querySelectorAll<HTMLElement>(`[${CHROME_STAGE_ATTR}]`));
  if (sources.length === 0 || sources.length !== targets.length) return null;

  // Order by the data-attribute index so source[i] maps to target[i] regardless
  // of DOM order.
  const byIndex = (attr: string) => (a: HTMLElement, b: HTMLElement) =>
    Number(a.getAttribute(attr)) - Number(b.getAttribute(attr));
  sources.sort(byIndex(INTRO_STAGE_ATTR));
  targets.sort(byIndex(CHROME_STAGE_ATTR));

  const base = container.getBoundingClientRect();
  const count = sources.length;

  return sources.map((src, i): MorphGhost => {
    const s = src.getBoundingClientRect();
    const t = targets[i].getBoundingClientRect();
    const startCx = s.left - base.left + s.width / 2;
    const startCy = s.top - base.top + s.height / 2;
    const endCx = t.left - base.left + t.width / 2;
    const endCy = t.top - base.top + t.height / 2;
    return {
      key: `ghost-${i}`,
      kind: kindFor(i, count),
      numeral: i < count - 1 ? String(i + 1) : null,
      startCx,
      startCy,
      dx: endCx - startCx,
      dy: endCy - startCy,
      fromW: s.width,
      fromH: s.height,
      midH: s.height * 0.72,
      toW: t.width,
      toH: t.height,
      delay: CUST_ANIM.MORPH_HERO_DELAY_MS + i * CUST_ANIM.MORPH_STAGGER_MS,
    };
  });
};

/** Total time from tap to the real-rail handoff: the last box's land + a buffer. */
export const morphTotalMs = (ghosts: MorphGhost[]): number =>
  ghosts.reduce((max, g) => Math.max(max, g.delay), 0) + CUST_ANIM.MORPH_FLIGHT_MS + CUST_ANIM.MORPH_TAIL_MS;
